/* ╔══════════════════════════════════════════════════════════════════╗
   ║  La Boutique Saint-Pierre — gravure en direct                       ║
   ║                                                                    ║
   ║  • BSPFourchette : pilote un aperçu .bsp-fk (texte gravé sur le     ║
   ║    manche, ajusté à la largeur, lettres « brûlées » au laser).      ║
   ║  • <bsp-demo-gravure> : démo du hero (prénoms qui défilent, champ   ║
   ║    d'essai, lien vers la fiche avec ?prenom=).                       ║
   ║  • <bsp-configurateur> : quantité, gravures (une par fourchette ou  ║
   ║    identique), liste collée, pochettes, paliers de prix, ajout au   ║
   ║    panier avec propriétés de ligne, modification depuis le panier.  ║
   ╚══════════════════════════════════════════════════════════════════╝ */
(() => {
  if (window.BSPGravure) return;

  const reduitMouvement = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Lettres latines (accents compris), chiffres et ponctuation simple : ce que l'atelier sait graver.
  const CARACTERE_OK = /^[\p{Script=Latin}\p{N} '’\-&.,!?+/()°#@:]$/u;

  function nettoyer(valeur, max) {
    const refuses = [];
    let propre = '';
    for (const c of Array.from(valeur.normalize('NFC'))) {
      if (CARACTERE_OK.test(c)) propre += c;
      else if (!/\s/.test(c)) refuses.push(c);
      else propre += ' ';
    }
    propre = propre.replace(/\s{2,}/g, ' ');
    if (max) propre = Array.from(propre).slice(0, max).join('');
    return { valeur: propre, refuses };
  }

  // Mise en forme de la gravure (comme sur les fourchettes de l'atelier) : la première lettre
  // de chaque mot en grande capitale, le reste en petites capitales — quelle que soit la casse tapée.
  function formatGravure(valeur) {
    return (valeur || '').toLocaleLowerCase('fr').replace(/(^|[\s\-'’])(\p{L})/gu, (m, sep, l) => sep + l.toLocaleUpperCase('fr'));
  }

  function decoderTextes(textes) {
    const zone = document.createElement('textarea');
    const propre = {};
    Object.keys(textes).forEach((k) => {
      const v = textes[k];
      if (typeof v === 'string' && v.includes('&') && !/[<>]/.test(v)) { zone.innerHTML = v; propre[k] = zone.value; } else propre[k] = v;
    });
    return propre;
  }

  function formatPrix(centimes, format) {
    const fmt = format || (window.BSP && window.BSP.formatPrix) || '{{amount}} CHF';
    const avecSep = (n, dec, milliers, decimal) => {
      const fixe = (n / 100).toFixed(dec);
      const [ent, frac] = fixe.split('.');
      const e = ent.replace(/\B(?=(\d{3})+(?!\d))/g, milliers);
      return frac ? e + decimal + frac : e;
    };
    const rond = centimes % 100 === 0;
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, cle) => {
      switch (cle) {
        case 'amount_no_decimals': return avecSep(centimes, 0, ',', '.');
        case 'amount_with_comma_separator': return rond ? avecSep(centimes, 0, '.', ',') : avecSep(centimes, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return avecSep(centimes, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return rond ? avecSep(centimes, 0, "'", '.') : avecSep(centimes, 2, "'", '.');
        case 'amount_with_space_separator': return rond ? avecSep(centimes, 0, ' ', ',') : avecSep(centimes, 2, ' ', ',');
        default: return rond ? avecSep(centimes, 0, ',', '.') : avecSep(centimes, 2, ',', '.');
      }
    });
  }

  /* ─── Une fourchette ─────────────────────────────────────────────── */
  class BSPFourchette {
    constructor(el) {
      this.el = el;
      this.zone = el.querySelector('.bsp-fk__gravure');
      this.texte = el.querySelector('.bsp-fk__texte');
      this.valeur = this.texte ? formatGravure(this.texte.textContent) : '';
      if (this.texte && this.valeur) this._construire(this.valeur, 0, false);
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(() => this.ajuster());
        this._ro.observe(el);
      }
      document.fonts && document.fonts.ready.then(() => this.ajuster());
    }

    _construire(valeur, depuis, animer) {
      const t = this.texte;
      while (t.childNodes.length > depuis) t.removeChild(t.lastChild);
      if (t.firstChild && t.firstChild.nodeType === 3) t.textContent = '';
      Array.from(valeur).slice(depuis).forEach((c, i) => {
        const s = document.createElement('span');
        s.textContent = c;
        if (animer && !reduitMouvement && c !== ' ') {
          s.className = 'is-chaud';
          s.style.setProperty('--d', `${i * 55}ms`);
          s.addEventListener('animationend', () => { s.className = ''; s.style.removeProperty('--d'); }, { once: true });
        }
        t.appendChild(s);
      });
    }

    graver(valeur, { animer = true } = {}) {
      if (!this.texte) return;
      valeur = formatGravure(valeur);
      const ancien = Array.from(this.valeur);
      const nouveau = Array.from(valeur);
      let prefixe = 0;
      while (prefixe < ancien.length && prefixe < nouveau.length && ancien[prefixe] === nouveau[prefixe]) prefixe++;
      // Les lettres déjà gravées ne bougent pas : seule la suite est (re)brûlée.
      const enfants = this.texte.querySelectorAll('span').length;
      this._construire(valeur, Math.min(prefixe, enfants), animer && nouveau.length > prefixe);
      this.valeur = valeur;
      this.ajuster();
    }

    effacer() {
      return new Promise((ok) => {
        if (!this.valeur || reduitMouvement) { this.graver('', { animer: false }); ok(); return; }
        this.texte.animate([{ opacity: 1, filter: 'blur(0)' }, { opacity: 0, filter: 'blur(2px)' }], { duration: 380, easing: 'ease-in' }).onfinish = () => {
          this.graver('', { animer: false });
          ok();
        };
      });
    }

    ajuster() {
      if (!this.texte || !this.zone) return;
      this.texte.style.setProperty('--fit', 1);
      const dispo = this.zone.clientWidth * 0.92;
      const largeur = this.texte.scrollWidth;
      if (!dispo || !largeur) return;
      const ratio = Math.max(0.5, Math.min(1, dispo / largeur));
      this.texte.style.setProperty('--fit', ratio.toFixed(3));
    }
  }

  /* ─── Démo du hero ───────────────────────────────────────────────── */
  class BSPDemoGravure extends HTMLElement {
    connectedCallback() {
      this.fk = new BSPFourchette(this.querySelector('.bsp-fk'));
      this.champ = this.querySelector('[data-demo-champ]');
      this.lien = this.querySelector('[data-demo-lien]');
      this.max = parseInt(this.dataset.max || '20', 10);
      try { this.noms = JSON.parse(this.dataset.noms || '[]').filter(Boolean); } catch (e) { this.noms = []; }
      this.actif = true;
      this.index = 0;

      if (this.champ) {
        this.champ.addEventListener('focus', () => this.arreter());
        this.champ.addEventListener('input', () => {
          this.arreter();
          const { valeur } = nettoyer(this.champ.value, this.max);
          if (valeur !== this.champ.value) this.champ.value = valeur;
          this.fk.graver(valeur);
          this.majLien(valeur);
        });
      }
      if (this.noms.length && !reduitMouvement) {
        this._io = new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; if (this.visible && this.actif && !this._tourne) this.boucle(); }, { threshold: 0.2 });
        this._io.observe(this);
      } else if (this.noms.length) {
        this.fk.graver(this.noms[0], { animer: false });
      }
    }

    disconnectedCallback() { this.actif = false; this._io && this._io.disconnect(); }

    majLien(valeur) {
      if (!this.lien) return;
      const url = new URL(this.lien.getAttribute('href'), location.origin);
      if (valeur) url.searchParams.set('prenom', valeur); else url.searchParams.delete('prenom');
      this.lien.setAttribute('href', url.pathname + url.search);
    }

    arreter() {
      if (!this.actif) return;
      this.actif = false;
      clearTimeout(this._t);
      this.fk.graver(this.champ && this.champ.value ? this.champ.value : '', { animer: false });
    }

    attendre(ms) { return new Promise((ok) => { this._t = setTimeout(ok, ms); }); }

    async boucle() {
      this._tourne = true;
      while (this.actif && this.visible) {
        const nom = this.noms[this.index % this.noms.length];
        const lettres = Array.from(nom);
        for (let i = 1; i <= lettres.length && this.actif; i++) {
          this.fk.graver(lettres.slice(0, i).join(''));
          if (this.champ && !this.champ.value) this.champ.setAttribute('placeholder', lettres.slice(0, i).join(''));
          await this.attendre(95 + Math.random() * 70);
        }
        if (!this.actif) break;
        await this.attendre(1900);
        if (!this.actif) break;
        await this.fk.effacer();
        await this.attendre(350);
        this.index++;
      }
      this._tourne = false;
      if (this.champ && !this.actif) this.champ.setAttribute('placeholder', this.champ.dataset.placeholder || '');
    }
  }

  /* ─── Configurateur ──────────────────────────────────────────────── */
  class BSPConfigurateur extends HTMLElement {
    connectedCallback() {
      try { this.cfg = JSON.parse(this.querySelector('[data-config]').textContent); } catch (e) { console.error('[BSP] configuration illisible', e); return; }
      // Le filtre « t » de Shopify encode les apostrophes (&#39;) : on les décode pour textContent.
      this.t = decoderTextes(this.cfg.textes || {});
      this.max = this.cfg.maxCaracteres || 20;
      this.etat = { quantite: this.cfg.quantiteDepart || 1, mode: 'unique', noms: [''], identique: '', actif: 0, pochettes: {}, saisie: 'champs', modifier: null, modifierPochettes: [], logoExistant: null };
      this.logo = null;        // fichier SVG choisi pour la pochette à votre logo
      this.logoParEmail = false;
      this.dejaAuPanier = 0;

      this.$ = (s) => this.querySelector(s);
      this.atelier = this.$('[data-atelier]');
      this.pile = this.$('[data-pile]');
      this.modeleFk = this.$('template[data-modele-fourchette]');
      this.liste = this.$('[data-liste-noms]');
      this.zoneCollage = this.$('[data-collage]');
      this.champCollage = this.$('[data-collage-champ]');
      this.qteInput = this.$('[data-qte-input]');
      this.bouton = this.$('[data-ajouter]');
      // Mobile : gros plan sur le manche en cours (étape 02) et barre d'ajout fixe
      this.loupe = this.$('[data-loupe]');
      this.loupeFk = this.loupe ? new BSPFourchette(this.loupe.querySelector('.bsp-fk')) : null;
      this.barre = this.$('[data-barre]');

      this.restaurer();
      const params = new URLSearchParams(location.search);
      if (params.get('prenom')) { this.etat.noms[0] = nettoyer(params.get('prenom'), this.max).valeur; }
      if (params.get('quantite')) this.etat.quantite = this.borner(parseInt(params.get('quantite'), 10));

      this.lier();
      this.rendreTout();
      this.initBarre();

      if (params.get('modifier')) this.chargerDepuisPanier(params.get('modifier'));
      else this.lirePanier();

      this.addEventListener('bsp:galerie', () => this.voirApercu());
    }

    borner(n) { return Math.max(1, Math.min(this.cfg.maxQuantite || 500, isNaN(n) ? 1 : n)); }

    /* Quitte la photo affichée (vignette) pour revenir à la fourchette gravée. */
    voirApercu() {
      if (!this.atelier || !this.atelier.classList.contains('is-photo')) return;
      this.dispatchEvent(new CustomEvent('bsp:apercu'));
      this.atelier.classList.remove('is-photo');
    }

    /* — persistance légère (brouillon de ce visiteur) — */
    restaurer() {
      try {
        const b = JSON.parse(localStorage.getItem('bsp-brouillon') || 'null');
        if (b && b.v === 1 && Date.now() - b.date < 7 * 864e5) {
          Object.assign(this.etat, { quantite: this.borner(b.quantite), mode: b.mode || 'unique', noms: b.noms || [''], identique: b.identique || '', pochettes: b.pochettes || {} });
        }
      } catch (e) { /* stockage indisponible : on repart de zéro */ }
    }
    sauver() {
      try {
        const { quantite, mode, noms, identique, pochettes } = this.etat;
        localStorage.setItem('bsp-brouillon', JSON.stringify({ v: 1, date: Date.now(), quantite, mode, noms, identique, pochettes }));
      } catch (e) { /* rien */ }
    }
    oublier() { try { localStorage.removeItem('bsp-brouillon'); } catch (e) { /* rien */ } }

    /* — événements — */
    lier() {
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-action]');
        if (!b || !this.contains(b)) return;
        const a = b.dataset.action;
        // Toute action qui touche aux fourchettes ramène à l'aperçu gravé.
        if (['moins', 'plus', 'preset', 'mode', 'saisie', 'ajuster-qte', 'precedent', 'suivant', 'vers-apercu'].includes(a)) this.voirApercu();
        if (a === 'moins') this.setQuantite(this.etat.quantite - 1);
        if (a === 'plus') this.setQuantite(this.etat.quantite + 1);
        if (a === 'preset') this.setQuantite(parseInt(b.dataset.valeur, 10));
        if (a === 'mode') this.setMode(b.dataset.valeur);
        if (a === 'saisie') this.setSaisie(b.dataset.valeur);
        if (a === 'ajuster-qte') this.setQuantite(this.lignesCollees().length);
        if (a === 'precedent') this.activer(this.etat.actif - 1, true);
        if (a === 'suivant') this.activer(this.etat.actif + 1, true);
        if (a === 'pochette-moins' || a === 'pochette-plus') this.pasPochette(b.dataset.pochetteId, a === 'pochette-plus' ? 1 : -1);
        if (a === 'pochettes-conseil') this.appliquerConseil();
        if (a === 'ajouter-barre') this.ajouter(false);
        if (a === 'logo-retirer') this.choisirLogo(null);
        if (a === 'logo-email') { this.logoParEmail = true; this.logo = null; this.rendreLogo(); this.ajouter(true); }
        if (a === 'retirer-vides') this.retirerVides();
        if (a === 'completer') this.focusPremierVide();
      });

      if (this.qteInput) {
        this.qteInput.addEventListener('change', () => { this.voirApercu(); this.setQuantite(parseInt(this.qteInput.value, 10)); });
        this.qteInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.qteInput.blur(); } });
      }

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.matches('[data-pochette-input], [data-propriete]')) { e.preventDefault(); e.target.blur(); }
      });
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-logo-input]')) { this.choisirLogo(e.target.files && e.target.files[0]); return; }
        const champ = e.target.closest('[data-pochette-input]');
        if (champ) this.setPochetteQte(champ.dataset.pochetteId, parseInt(champ.value, 10), true);
      });

      const identique = this.$('[data-identique]');
      if (identique) {
        identique.addEventListener('input', () => {
          const { valeur, refuses } = nettoyer(identique.value, this.max);
          if (valeur !== identique.value) identique.value = valeur;
          this.signalerRefus(identique, refuses);
          this.etat.identique = valeur;
          this.majCompteur(identique);
          this.majPile();
          this.sauver();
        });
        identique.addEventListener('focus', () => this.voirApercu());
      }

      if (this.champCollage) {
        this.champCollage.addEventListener('input', () => {
          const lignes = this.lignesCollees();
          lignes.forEach((l, i) => { if (i < this.etat.quantite) this.etat.noms[i] = l; });
          for (let i = lignes.length; i < this.etat.quantite; i++) this.etat.noms[i] = '';
          this.majCollage();
          this.majPile();
          this.sauver();
          suivreCurseur();
        });
        // La ligne où se trouve le curseur = la fourchette montrée en gros plan.
        const suivreCurseur = () => {
          const avant = this.champCollage.value.slice(0, this.champCollage.selectionStart || 0).split(/[\n;\t]/);
          const index = avant.slice(0, -1).filter((l) => l.trim()).length;
          if (index !== this.etat.actif && index < this.etat.quantite) this.activer(index, true);
        };
        ['click', 'keyup', 'focus'].forEach((ev) => this.champCollage.addEventListener(ev, suivreCurseur));
      }

      this.pile && this.pile.addEventListener('click', (e) => {
        const fk = e.target.closest('[data-index]');
        if (fk) this.activer(parseInt(fk.dataset.index, 10), true, true);
      });

      const form = this.$('form');
      form && form.addEventListener('submit', (e) => { e.preventDefault(); this.ajouter(false); });
    }

    /* — quantité / mode — */
    setQuantite(n) {
      n = this.borner(n);
      this.etat.quantite = n;
      while (this.etat.noms.length < n) this.etat.noms.push('');
      if (this.etat.actif >= n) this.etat.actif = n - 1;
      if (n > 30 && this.etat.mode === 'unique' && this.etat.saisie === 'champs' && !this._saisieChoisie) this.etat.saisie = 'liste';
      this.rendreTout();
      this.sauver();
    }

    setMode(mode) {
      this.etat.mode = mode === 'identique' ? 'identique' : 'unique';
      if (this.etat.mode === 'identique' && !this.etat.identique) this.etat.identique = this.etat.noms.find(Boolean) || '';
      this.rendreTout();
      this.sauver();
      const cible = this.etat.mode === 'identique' ? this.$('[data-identique]') : this.liste.querySelector('input');
      cible && cible.focus({ preventScroll: true });
    }

    setSaisie(s) {
      this._saisieChoisie = true;
      this.etat.saisie = s === 'liste' ? 'liste' : 'champs';
      if (this.etat.saisie === 'liste' && this.champCollage) this.champCollage.value = this.etat.noms.slice(0, this.etat.quantite).join('\n').replace(/\n+$/, '');
      this.rendreNoms();
      if (this.etat.saisie === 'liste' && this.champCollage) this.champCollage.focus();
    }

    lignesCollees() {
      if (!this.champCollage) return [];
      let brut = this.champCollage.value;
      if (!/[\n;\t]/.test(brut) && brut.includes(',')) brut = brut.replace(/,/g, '\n');
      return brut.split(/[\n;\t]/).map((l) => nettoyer(l.trim(), this.max).valeur.trim()).filter(Boolean);
    }

    /* — pochettes : une quantité par modèle (standard, grande, à votre logo…) — */
    pochettesDispo() { return this.cfg.pochettes || []; }
    pochette(id) { return this.pochettesDispo().find((p) => p.id === id) || null; }
    // Toutes les pochettes sont proposées ; celle à votre logo a un minimum de pochettes (voir setPochetteQte).
    pochetteOuverte() { return true; }
    qtePochette(id) {
      const p = this.pochette(id);
      return p && this.pochetteOuverte(p) ? (this.etat.pochettes[id] || 0) : 0;
    }
    pochettesChoisies() { return this.pochettesDispo().map((p) => ({ p, n: this.qtePochette(p.id) })).filter((x) => x.n > 0); }
    setPochetteQte(id, n, saisie) {
      const p = this.pochette(id);
      if (!p) return;
      let v = Math.max(0, Math.min(9999, isNaN(n) ? 0 : n));
      // Minimum de pochettes (à votre logo) : 0 ou au moins le minimum. Une saisie monte au minimum ;
      // le bouton « − » sous le minimum revient à 0, le « + » depuis 0 saute au minimum.
      if (p.minPochettes && v > 0 && v < p.minPochettes) v = saisie || v > (this.etat.pochettes[id] || 0) ? p.minPochettes : 0;
      this.etat.pochettes[id] = v;
      this.rendrePochettes();
      this.rendreResume();
      this.sauver();
    }
    pasPochette(id, sens) { this.setPochetteQte(id, this.qtePochette(id) + sens); }

    /* — logo de la pochette personnalisée : SVG, sans blanc, joint à la ligne de commande — */
    logoDemande() {
      const p = this.pochette('p3');
      return !!(p && p.logo && this.qtePochette('p3') > 0);
    }
    alerteLogo(message, info) {
      const alerte = this.$('[data-logo-alerte]');
      if (!alerte) return;
      alerte.textContent = message || '';
      alerte.hidden = !message;
      alerte.classList.toggle('is-info', !!info);
    }
    async choisirLogo(fichier) {
      const champ = this.$('[data-logo-input]');
      this.logo = null;
      this.logoParEmail = false;
      this._logoEnvoye = null;
      const secours = this.$('[data-logo-secours]');
      if (secours) secours.hidden = true;
      this.alerteLogo('');
      if (fichier) {
        const estSvg = /\.svg$/i.test(fichier.name) || fichier.type === 'image/svg+xml';
        let contenu = '';
        if (estSvg && fichier.size <= 5 * 1024 * 1024) { try { contenu = await fichier.text(); } catch (e) { /* illisible */ } }
        if (fichier.size > 5 * 1024 * 1024) this.alerteLogo(this.t.logoPoids);
        else if (!estSvg || !/<svg[\s>]/i.test(contenu)) this.alerteLogo(this.t.logoFormat);
        else {
          this.logo = fichier;
          // Le blanc ne s'imprime pas sur la toile : on prévient sans bloquer (un fond blanc oublié est fréquent).
          const blanc = /(?:fill|stroke|stop-color|color)\s*[:=]\s*["']?\s*(?:#fff(?:fff)?(?![0-9a-f])|white\b|rgb\(\s*255\s*,\s*255\s*,\s*255\s*\))/i.test(contenu);
          if (blanc) this.alerteLogo(this.t.logoBlanc, true);
        }
      }
      if (!this.logo && champ) champ.value = '';
      this.rendreLogo();
    }
    rendreLogo() {
      const zone = this.$('[data-logo]');
      if (!zone) return;
      zone.hidden = !this.logoDemande();
      const deja = this.etat.logoExistant;
      const nom = zone.querySelector('[data-logo-nom]');
      if (nom) nom.textContent = this.logo ? this.logo.name : this.logoParEmail ? (this.t.logoParEmail || '') : deja ? (this.t.logoDeja || '') : '';
      const libelle = zone.querySelector('[data-logo-libelle]');
      if (libelle) libelle.textContent = (this.logo || deja) ? (this.t.logoChanger || 'Changer de fichier') : (this.t.logoChoisir || 'Choisir un fichier SVG');
      const retirer = zone.querySelector('[data-logo-retirer]');
      if (retirer) retirer.hidden = !this.logo;
      const apercu = zone.querySelector('[data-logo-apercu]');
      const image = zone.querySelector('[data-logo-image]');
      if (this._logoUrl) { URL.revokeObjectURL(this._logoUrl); this._logoUrl = null; }
      const source = this.logo ? (this._logoUrl = URL.createObjectURL(this.logo)) : (deja && /^https?:/.test(deja) ? deja : '');
      if (apercu && image) { apercu.hidden = !source; if (source) image.src = source; else image.removeAttribute('src'); }
    }

    // Conseil : des grandes pochettes pour les lots de 8 et plus, des standard pour le reste.
    pochettesConseillees() {
      const q = this.etat.quantite;
      const std = this.pochette('p1');
      const gde = this.pochette('p2');
      const r = {};
      if (std && gde) {
        let g = Math.floor(q / gde.capacite);
        let reste = q % gde.capacite;
        if (reste >= 2 * std.capacite) { g += 1; reste = 0; }
        if (g) r.p2 = g;
        if (reste) r.p1 = Math.ceil(reste / std.capacite);
      } else if (std || gde) {
        const p = std || gde;
        r[p.id] = Math.ceil(q / p.capacite);
      }
      return r;
    }
    appliquerConseil() {
      const c = this.pochettesConseillees();
      ['p1', 'p2'].forEach((id) => { if (this.pochette(id)) this.etat.pochettes[id] = c[id] || 0; });
      this.rendrePochettes();
      this.rendreResume();
      this.sauver();
    }

    /* — prix — */
    // Particuliers / revendeurs : montant de n fourchettes (les premières au prix normal, les suivantes au prix réduit).
    montantTranche(n) {
      const tr = this.cfg.tranche;
      const k = Math.min(n, tr.des - 1);
      return k * this.cfg.prixBase + Math.max(0, n - k) * tr.prix;
    }

    palier(qteTotale) {
      const paliers = (this.cfg.paliers || []).slice().sort((a, b) => a.min - b.min);
      let actuel = paliers[0] || { min: 1, prix: this.cfg.prixBase };
      let suivant = null;
      for (const p of paliers) { if (qteTotale >= p.min) actuel = p; else { suivant = p; break; } }
      return { actuel, suivant, paliers };
    }

    /* — rendu — */
    rendreTout() {
      if (this.qteInput) this.qteInput.value = this.etat.quantite;
      this.querySelectorAll('[data-action="preset"]').forEach((b) => b.setAttribute('aria-pressed', String(parseInt(b.dataset.valeur, 10) === this.etat.quantite)));
      const blocMode = this.$('[data-bloc-mode]');
      if (blocMode) blocMode.hidden = this.etat.quantite < 2;
      if (this.etat.quantite < 2) this.etat.mode = 'unique';
      this.querySelectorAll('[data-action="mode"]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.valeur === this.etat.mode)));
      this.querySelectorAll('[data-mode-zone]').forEach((z) => { z.hidden = z.dataset.modeZone !== this.etat.mode; });
      const identique = this.$('[data-identique]');
      if (identique && identique.value !== this.etat.identique) { identique.value = this.etat.identique; this.majCompteur(identique); }
      const nbIdentique = this.$('[data-nb-identique]');
      if (nbIdentique) nbIdentique.textContent = this.etat.quantite;
      this.rendreNoms();
      this.rendrePile();
      this.rendrePochettes();
      this.rendreResume();
    }

    rendreNoms() {
      const enListe = this.etat.saisie === 'liste';
      this.querySelectorAll('[data-action="saisie"]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.valeur === this.etat.saisie)));
      if (this.zoneCollage) this.zoneCollage.hidden = !enListe;
      if (this.liste) this.liste.hidden = enListe;
      const basculeSaisie = this.$('[data-bascule-saisie]');
      if (basculeSaisie) basculeSaisie.hidden = this.etat.quantite < 2;
      if (enListe) { this.majCollage(); return; }

      const n = this.etat.quantite;
      const rangees = this.liste.querySelectorAll('[data-rangee]');
      // Ajouter les rangées manquantes
      for (let i = rangees.length; i < n; i++) this.liste.appendChild(this.creerRangee(i));
      // Retirer les rangées en trop
      for (let i = rangees.length - 1; i >= n; i--) rangees[i].remove();
      this.liste.querySelectorAll('[data-rangee]').forEach((r, i) => {
        const input = r.querySelector('input');
        if (document.activeElement !== input && input.value !== (this.etat.noms[i] || '')) { input.value = this.etat.noms[i] || ''; }
        this.majCompteur(input);
        r.classList.toggle('is-actif', i === this.etat.actif);
      });
    }

    creerRangee(i) {
      const r = document.createElement('div');
      r.className = 'bsp-cfg__rangee';
      r.dataset.rangee = i;
      const id = `bsp-nom-${this.cfg.sectionId}-${i}`;
      r.innerHTML = `
        <label class="bsp-cfg__num" for="${id}"><span class="bsp-sr">${this.t.gravureFourchette || 'Gravure de la fourchette'} </span>${String(i + 1).padStart(2, '0')}</label>
        <input class="bsp-cfg__nom" id="${id}" type="text" maxlength="${this.max}" autocomplete="off" autocapitalize="words" spellcheck="false" enterkeyhint="next" placeholder="${(this.t.placeholderNom || 'Prénom {n}').replace('{n}', i + 1)}">
        <span class="bsp-cfg__compte" aria-hidden="true"></span>`;
      const input = r.querySelector('input');
      input.value = this.etat.noms[i] || '';
      input.addEventListener('focus', () => { this.activer(i, true); this.voirApercu(); });
      input.addEventListener('input', () => {
        const { valeur, refuses } = nettoyer(input.value, this.max);
        if (valeur !== input.value) { const pos = input.selectionStart; input.value = valeur; try { input.setSelectionRange(pos - 1, pos - 1); } catch (e) { /* rien */ } }
        this.signalerRefus(input, refuses);
        this.etat.noms[i] = valeur;
        this.majCompteur(input);
        this.majFourchette(i);
        r.classList.remove('is-erreur');
        this.$('[data-vides]') && (this.$('[data-vides]').hidden = true);
        this.sauver();
      });
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const suivant = this.liste.querySelector(`[data-rangee="${i + 1}"] input`);
          if (suivant) suivant.focus(); else this.bouton && this.bouton.focus();
        }
      });
      return r;
    }

    majCompteur(input) {
      const c = input.parentElement.querySelector('.bsp-cfg__compte');
      if (!c) return;
      const n = Array.from(input.value).length;
      c.textContent = `${n}/${this.max}`;
      c.classList.toggle('is-visible', n >= this.max - 6);
      c.classList.toggle('is-max', n >= this.max);
    }

    signalerRefus(input, refuses) {
      const msg = this.$('[data-refus]');
      if (!msg) return;
      if (refuses.length) {
        msg.textContent = (this.t.caractereRefuse || 'Ce caractère ne peut pas être gravé : {c}').replace('{c}', refuses.join(' '));
        msg.hidden = false;
        clearTimeout(this._refusT);
        this._refusT = setTimeout(() => { msg.hidden = true; }, 3200);
      }
    }

    majCollage() {
      const info = this.$('[data-collage-info]');
      if (!info) return;
      const nb = this.lignesCollees().length;
      const q = this.etat.quantite;
      const ajuster = this.$('[data-action="ajuster-qte"]');
      info.textContent = (this.t.collageInfo || '{n} gravure(s) pour {q} fourchette(s)').replace('{n}', nb).replace('{q}', q);
      info.classList.toggle('is-ok', nb === q);
      if (ajuster) { ajuster.hidden = nb === 0 || nb === q; ajuster.textContent = (this.t.ajusterQte || 'Passer à {n} fourchettes').replace('{n}', nb); }
    }

    /* — atelier (aperçu) — */
    texteDe(i) { return this.etat.mode === 'identique' ? this.etat.identique : (this.etat.noms[i] || ''); }

    rendrePile() {
      if (!this.pile || !this.modeleFk) return;
      const unique = this.atelier && this.atelier.dataset.style === 'unique';
      const n = this.etat.mode === 'identique' ? (unique ? 1 : Math.min(this.etat.quantite, this.cfg.maxPile || 6)) : this.etat.quantite;
      const actuelles = this.pile.querySelectorAll('[data-index]');
      const nouvelles = [];
      for (let i = actuelles.length; i < n; i++) {
        const noeud = this.modeleFk.content.firstElementChild.cloneNode(true);
        noeud.dataset.index = i;
        noeud.style.setProperty('--i', i);
        noeud.querySelector('.bsp-atelier__num').textContent = String(i + 1).padStart(2, '0');
        noeud.classList.add('is-entree');
        this.pile.appendChild(noeud);
        noeud._fk = new BSPFourchette(noeud.querySelector('.bsp-fk'));
        noeud._fk.graver(this.texteDe(i), { animer: false });
        nouvelles.push(noeud);
      }
      // Pas de requestAnimationFrame (bloqué en arrière-plan) : un reflow suffit pour lancer la transition.
      if (nouvelles.length) { void this.pile.offsetWidth; nouvelles.forEach((noeud) => noeud.classList.remove('is-entree')); }
      for (let i = actuelles.length - 1; i >= n; i--) {
        const noeud = actuelles[i];
        noeud.classList.add('is-sortie');
        noeud.removeAttribute('data-index');
        setTimeout(() => noeud.remove(), 320);
      }
      this.atelier.dataset.nombre = Math.min(n, 9);
      this.atelier.classList.toggle('is-multiple', n > 1);
      this.atelier.classList.toggle('is-identique', this.etat.mode === 'identique');
      const badge = this.$('[data-badge-identique]');
      if (badge) { badge.hidden = !(this.etat.mode === 'identique' && this.etat.quantite > 1); badge.textContent = `× ${this.etat.quantite}`; }
      this.majPile();
      // Pile : recadrer une fois les fourchettes retirées sorties du flux.
      if (!unique) { clearTimeout(this._recadre); this._recadre = setTimeout(() => this.centrer(this.etat.actif), 340); }
    }

    majPile() {
      if (!this.pile) return;
      this.pile.querySelectorAll('[data-index]').forEach((noeud) => {
        const i = parseInt(noeud.dataset.index, 10);
        noeud._fk && noeud._fk.graver(this.texteDe(i), { animer: true });
      });
      this.activer(this.etat.actif, false);
    }

    majFourchette(i) {
      const noeud = this.pile && this.pile.querySelector(`[data-index="${i}"]`);
      noeud && noeud._fk && noeud._fk.graver(this.texteDe(i));
      if (i === this.etat.actif) this.majLoupe();
    }

    activer(i, defiler, focus) {
      const n = this.etat.quantite;
      i = Math.max(0, Math.min(n - 1, i));
      this.etat.actif = i;
      const enIdentique = this.etat.mode === 'identique';
      const unique = this.atelier && this.atelier.dataset.style === 'unique';
      if (this.pile) {
        // Aperçu « une fourchette » (maquette) : la fourchette en cours de saisie, ou la première en mode identique.
        this.pile.querySelectorAll('[data-index]').forEach((noeud) => {
          const k = parseInt(noeud.dataset.index, 10);
          noeud.classList.toggle('is-actif', enIdentique ? (unique && k === 0) : k === i);
        });
        if (defiler !== false && !unique) this.centrer(i);
      }
      this.liste && this.liste.querySelectorAll('[data-rangee]').forEach((r, k) => r.classList.toggle('is-actif', k === i));
      const compteur = this.$('[data-compteur-pile]');
      if (compteur) compteur.textContent = enIdentique ? `× ${String(n).padStart(2, '0')}` : `${String(i + 1).padStart(2, '0')} / ${String(n).padStart(2, '0')}`;
      const nav = this.$('[data-nav-pile]');
      if (nav) nav.hidden = enIdentique || n < 2;
      this.majLoupe();
      if (focus) {
        const input = enIdentique ? this.$('[data-identique]') : this.liste.querySelector(`[data-rangee="${i}"] input`);
        if (input && this.etat.saisie === 'champs') input.focus({ preventScroll: window.innerWidth < 990 });
      }
    }

    // Le gros plan montre la fourchette en cours de saisie (ou la première en mode « le même partout »).
    majLoupe() {
      if (!this.loupeFk) return;
      const enIdentique = this.etat.mode === 'identique';
      const i = enIdentique ? 0 : this.etat.actif;
      const n = this.etat.quantite;
      this.loupeFk.graver(this.texteDe(i));
      const compteur = this.loupe.querySelector('[data-loupe-compteur]');
      if (compteur) compteur.textContent = enIdentique ? `× ${String(n).padStart(2, '0')}` : `${String(i + 1).padStart(2, '0')} / ${String(n).padStart(2, '0')}`;
    }

    // Barre fixe (mobile) : visible quand le bouton principal n'est pas à l'écran et qu'aucun champ n'a le focus.
    initBarre() {
      if (!this.barre || !this.bouton || !('IntersectionObserver' in window)) return;
      let boutonVisible = true;
      let atteint = false;   // on est arrivé au configurateur (ou on l'a dépassé)
      let saisie = false;
      const maj = () => this.barre.classList.toggle('is-visible', atteint && !boutonVisible && !saisie);
      new IntersectionObserver(([e]) => { boutonVisible = e.isIntersecting; maj(); }, { threshold: 0.4 }).observe(this.bouton);
      new IntersectionObserver(([e]) => { atteint = e.isIntersecting || e.boundingClientRect.top < 0; maj(); }).observe(this);
      const champTexte = (el) => el && el.matches && el.matches('input:not([type="file"]), textarea');
      this.addEventListener('focusin', (e) => { if (champTexte(e.target)) { saisie = true; maj(); } });
      this.addEventListener('focusout', () => { setTimeout(() => { saisie = champTexte(document.activeElement) && this.contains(document.activeElement); maj(); }, 80); });
    }

    centrer(i) {
      const fenetre = this.$('[data-fenetre]');
      const noeud = this.pile.querySelector(`[data-index="${i}"]`);
      if (!fenetre || !noeud) return;
      const max = this.pile.scrollHeight - fenetre.clientHeight;
      if (max <= 0) { this.pile.style.transform = ''; fenetre.classList.remove('has-haut', 'has-bas'); return; }
      const cible = noeud.offsetTop + noeud.offsetHeight / 2 - fenetre.clientHeight / 2;
      const y = Math.max(0, Math.min(max, cible));
      this.pile.style.transform = `translateY(${-y}px)`;
      fenetre.classList.toggle('has-haut', y > 4);
      fenetre.classList.toggle('has-bas', y < max - 4);
    }

    rendrePochettes() {
      this.querySelectorAll('[data-pochette]').forEach((ligne) => {
        const p = this.pochette(ligne.dataset.pochette);
        if (!p) return;
        const ouverte = this.pochetteOuverte(p);
        const n = this.qtePochette(p.id);
        ligne.classList.toggle('is-choisie', n > 0);
        ligne.classList.toggle('is-verrou', !ouverte);
        const champ = ligne.querySelector('[data-pochette-input]');
        if (champ) { champ.disabled = !ouverte; if (document.activeElement !== champ || champ.value === '') champ.value = n; }
        ligne.querySelectorAll('[data-action^="pochette-"]').forEach((b) => { b.disabled = !ouverte || (b.dataset.action === 'pochette-moins' && n < 1); });
        const verrou = ligne.querySelector('[data-pochette-verrou]');
        if (verrou) verrou.hidden = ouverte;
      });
      this.rendreLogo();
      // Conseil, masqué quand la sélection le suit déjà
      const zone = this.$('[data-pochette-conseil]');
      if (zone) {
        const c = this.pochettesConseillees();
        const ids = Object.keys(c);
        const suivi = ['p1', 'p2'].every((id) => (c[id] || 0) === this.qtePochette(id));
        // Inutile aussi quand les pochettes choisies rangent déjà toutes les fourchettes
        const capacite = this.pochettesChoisies().reduce((t, x) => t + x.n * (x.p.capacite || 1), 0);
        zone.hidden = !ids.length || suivi || capacite >= this.etat.quantite;
        const txt = zone.querySelector('[data-conseil-texte]');
        if (txt) {
          const detail = ids.map((id) => `${c[id]} × ${this.pochette(id).titre}`).join(' + ');
          txt.textContent = (this.t.conseilPochette || 'Pour {q} fourchette(s) : {detail}').replace('{q}', this.etat.quantite).replace('{detail}', detail);
        }
      }
    }

    rendreResume() {
      const q = this.etat.quantite;
      const total = this.dejaAuPanier + q;
      const { actuel, suivant, paliers } = this.palier(total);
      const base = this.cfg.prixBase;
      const tr = this.cfg.tranche;
      // Prix « tranche » : ce lot vient après les fourchettes déjà au panier (même produit).
      const sousTotal = tr ? this.montantTranche(total) - this.montantTranche(this.dejaAuPanier) : q * actuel.prix;
      const choisies = this.pochettesChoisies();
      const pochettes = choisies.reduce((t, x) => t + x.n * x.p.prix, 0);
      const economie = q * base - sousTotal;
      const f = (c) => formatPrix(c, this.cfg.formatPrix);

      const set = (sel, txt) => { const el = this.$(sel); if (el) el.textContent = txt; };
      // Prix affiché en gros : celui du palier atteint. Prix public : selon la quantité choisie seulement
      // (le panier ne joue que sur le récapitulatif, avec sa note) — pas de saut au chargement.
      set('[data-prix-unitaire]', f(tr ? base : this.cfg.prixDynamique === false ? this.palier(q).actuel.prix : actuel.prix));
      const pastille = this.$('[data-prix-des]');
      if (pastille) pastille.classList.toggle('is-atteint', actuel.min > 1);
      // Les deux offres (lot, puis prix réduit) s'allument quand la commande les atteint.
      this.querySelectorAll('[data-offre]').forEach((o) => o.classList.toggle('is-atteint', total >= parseInt(o.dataset.offre, 10)));
      set('[data-ligne-fourchettes]', (this.t.ligneFourchettes || '{q} fourchette(s) gravée(s) × {p}').replace('{q}', q).replace('{p}', f(actuel.prix)));
      // Prix plein, puis l'économie en dessous : le récapitulatif se lit comme une addition juste.
      set('[data-montant-fourchettes]', f(economie > 0 ? q * base : sousTotal));
      const lignesPochettes = this.$('[data-lignes-pochettes]');
      if (lignesPochettes) {
        lignesPochettes.replaceChildren(...choisies.map(({ p, n }) => {
          const l = document.createElement('div');
          l.className = 'bsp-totaux__ligne';
          const a = document.createElement('span'); a.textContent = `${n} × ${p.titre}`;
          const b = document.createElement('span'); b.textContent = f(n * p.prix);
          l.append(a, b);
          return l;
        }));
      }
      const ligneEco = this.$('[data-ligne-economie]');
      if (ligneEco) { ligneEco.hidden = economie <= 0; set('[data-montant-economie]', `− ${f(economie)}`); }
      set('[data-total]', f(sousTotal + pochettes));
      // Espace revendeurs : ce que coûteraient ces fourchettes au prix public (même tranche), et l'économie.
      const pub = this.cfg.public;
      const lignePub = this.$('[data-ligne-public]');
      if (pub && lignePub) {
        const montantPublic = (n) => { const k = Math.min(n, pub.des - 1); return k * pub.base + (n - k) * pub.suite; };
        const prixPublic = montantPublic(total) - montantPublic(this.dejaAuPanier);
        const eco = prixPublic - sousTotal;
        lignePub.hidden = eco <= 0;
        lignePub.textContent = (this.t.economiePublic || 'Vous économisez {montant} sur le prix public (−{pct} %)')
          .replace('{montant}', f(eco)).replace('{pct}', prixPublic ? Math.round((eco / prixPublic) * 100) : 0);
      }
      set('[data-barre-total]', f(sousTotal + pochettes));
      set('[data-barre-qte]', ((q > 1 ? this.t.barrePluriel : this.t.barreSingulier) || '{q} fourchette(s)').replace('{q}', q));
      set('[data-total-bouton]', f(sousTotal + pochettes));

      // Paliers
      this.querySelectorAll('[data-palier]').forEach((el) => {
        const min = parseInt(el.dataset.palier, 10);
        el.classList.toggle('is-actif', min === actuel.min);
        el.classList.toggle('is-atteint', min <= total);
      });
      const conseil = this.$('[data-conseil-palier]');
      if (conseil) {
        if (suivant) {
          const reste = suivant.min - total;
          conseil.innerHTML = (this.t.plusQue || 'Plus que <strong>{n}</strong> pour passer à <strong>{p}</strong> la pièce').replace('{n}', reste).replace('{p}', f(suivant.prix));
        } else {
          conseil.innerHTML = this.t.meilleurPrix || 'Vous bénéficiez du <strong>meilleur tarif</strong>';
        }
      }
      const jauge = this.$('[data-jauge-palier]');
      if (jauge && paliers.length) {
        const idx = paliers.indexOf(actuel);
        const debut = actuel.min;
        const fin = suivant ? suivant.min : actuel.min;
        const part = suivant ? (total - debut) / (fin - debut) : 1;
        jauge.style.width = `${((idx + Math.min(1, part)) / paliers.length) * 100}%`;
      }
      const noteDeja = this.$('[data-deja-panier]');
      if (noteDeja) {
        // Prix public : la note n'apparaît que si c'est le panier qui fait atteindre le palier.
        const parLePanier = actuel.min > 1 && q < actuel.min;
        noteDeja.hidden = this.dejaAuPanier < 1 || (!tr && this.cfg.prixDynamique === false && !parLePanier);
        noteDeja.textContent = (this.t.dejaPanier || 'Tarif calculé avec les {n} fourchette(s) déjà dans votre panier').replace('{n}', this.dejaAuPanier);
      }
      const live = this.$('[data-annonce]');
      if (live) { clearTimeout(this._liveT); this._liveT = setTimeout(() => { live.textContent = `${q} — ${f(sousTotal + pochettes)}`; }, 600); }
    }

    /* — panier — */
    async lirePanier(sauf) {
      const exclues = [].concat(sauf || []);
      try {
        const r = await fetch(`${window.BSP.routes.panier}.js`, { headers: { Accept: 'application/json' } });
        const panier = await r.json();
        this.dejaAuPanier = panier.items.filter((it) => it.product_id === this.cfg.produitId && !exclues.includes(it.key)).reduce((s, it) => s + it.quantity, 0);
        this.rendreResume();
        return panier;
      } catch (e) { return null; }
    }

    // Lignes de fourchettes d'un même lot (même _bsp), dans l'ordre des gravures.
    lignesDuLot(items, ligne) {
      const id = ligne.properties && ligne.properties._bsp;
      if (!id) return [ligne];
      const premier = (it) => Math.min(...Object.keys(it.properties || {}).map((k) => { const m = k.match(/^Gravure (\d+)$/); return m ? parseInt(m[1], 10) : Infinity; }));
      return items.filter((it) => it.product_id === ligne.product_id && it.properties && it.properties._gravure && it.properties._bsp === id)
        .sort((a, b) => (premier(a) - premier(b)) || (a.variant_id === this.cfg.varianteId ? -1 : 1));
    }

    // Noms gravés d'un ensemble de lignes (mode « même texte » : répété) ; identique = le texte s'il est le même partout.
    nomsDeLignes(lignes) {
      const vide = this.t.sansGravure || '— sans gravure —';
      const noms = [];
      const textes = new Set();
      let tousIdentiques = true;
      lignes.forEach((it) => {
        const props = it.properties || {};
        const cleId = Object.keys(props).find((k) => /^Gravure \(/.test(k));
        if (cleId) { for (let i = 0; i < it.quantity; i++) noms.push(props[cleId]); textes.add(props[cleId]); return; }
        tousIdentiques = false;
        Object.keys(props).map((k) => [k.match(/^Gravure (\d+)$/), props[k]]).filter(([m]) => m).sort((a, b) => a[0][1] - b[0][1]).forEach(([, v]) => noms.push(v));
      });
      return { noms: noms.map((n) => (n === vide ? '' : n)), identique: tousIdentiques && textes.size === 1 ? [...textes][0] : null };
    }

    async chargerDepuisPanier(cle) {
      const panier = await this.lirePanier(cle);
      const ligne = panier && panier.items.find((it) => it.key === cle);
      if (!ligne) return;
      const props = ligne.properties || {};
      const lot = this.lignesDuLot(panier.items, ligne);
      const { noms, identique } = this.nomsDeLignes(lot);
      this.etat.modifier = lot.map((it) => it.key);
      this.dejaAuPanier = panier.items.filter((it) => it.product_id === this.cfg.produitId && !this.etat.modifier.includes(it.key)).reduce((s, it) => s + it.quantity, 0);
      this.etat.quantite = noms.length || ligne.quantity;
      // Les pochettes du même lot (propriété _lot = _bsp de la ligne des fourchettes)
      this.etat.pochettes = {};
      this.etat.modifierPochettes = [];
      this.etat.logoExistant = null;
      if (props._bsp) {
        panier.items.filter((it) => it.properties && it.properties._lot === props._bsp).forEach((it) => {
          const p = this.pochettesDispo().find((x) => x.varianteId === it.variant_id);
          if (p) this.etat.pochettes[p.id] = (this.etat.pochettes[p.id] || 0) + it.quantity;
          if (p && p.id === 'p3' && it.properties.Logo) this.etat.logoExistant = it.properties.Logo;
          this.etat.modifierPochettes.push(it.key);
        });
      }
      if (identique !== null) { this.etat.mode = 'identique'; this.etat.identique = identique; }
      else { this.etat.mode = 'unique'; this.etat.noms = Array.from({ length: this.etat.quantite }, (_, i) => noms[i] || ''); }
      this.rendreTout();
      const etiquette = this.$('[data-libelle-bouton]');
      if (etiquette) etiquette.textContent = this.t.mettreAJour || 'Mettre à jour le panier';
      const bandeau = this.$('[data-bandeau-modif]');
      if (bandeau) bandeau.hidden = false;
    }

    nomsVides() {
      if (this.etat.mode === 'identique') return this.etat.identique.trim() ? [] : [0];
      const vides = [];
      for (let i = 0; i < this.etat.quantite; i++) if (!(this.etat.noms[i] || '').trim()) vides.push(i);
      return vides;
    }

    // Garde seulement les fourchettes qui ont un nom (dans l'ordre) et ajuste la quantité.
    retirerVides() {
      const gardes = this.etat.noms.slice(0, this.etat.quantite).map((n) => (n || '').trim()).filter(Boolean);
      if (!gardes.length) { this.focusPremierVide(); return; }
      const retirees = this.etat.quantite - gardes.length;
      this.etat.noms = gardes;
      this.etat.actif = 0;
      this.setQuantite(gardes.length);
      if (this.etat.saisie === 'liste' && this.champCollage) { this.champCollage.value = gardes.join('\n'); this.majCollage(); }
      this.liste && this.liste.querySelectorAll('.is-erreur').forEach((r) => r.classList.remove('is-erreur'));
      const zone = this.$('[data-vides]');
      if (zone) zone.hidden = true;
      const live = this.$('[data-annonce]');
      if (live) live.textContent = (this.t.videsRetires || '{n} fourchette(s) retirée(s)').replace('{n}', retirees);
      this.bouton && this.bouton.focus({ preventScroll: true });
      this.bouton && this.bouton.scrollIntoView({ block: 'center', behavior: reduitMouvement ? 'auto' : 'smooth' });
    }

    focusPremierVide() {
      const vides = this.nomsVides();
      const zone = this.$('[data-vides]');
      if (zone) zone.hidden = true;
      if (this.etat.mode === 'identique') { this.$('[data-identique]').focus(); return; }
      if (this.etat.saisie === 'liste') { this.champCollage.focus(); return; }
      const r = this.liste.querySelector(`[data-rangee="${vides[0]}"]`);
      if (r) { r.querySelector('input').focus(); r.scrollIntoView({ block: 'center', behavior: reduitMouvement ? 'auto' : 'smooth' }); }
    }

    proprietes() {
      const props = {};
      const q = this.etat.quantite;
      if (this.etat.mode === 'identique') {
        props[(this.t.cleIdentique || 'Gravure (identique ×{n})').replace('{n}', q)] = formatGravure(this.etat.identique.trim());
      } else {
        for (let i = 0; i < q; i++) props[`Gravure ${i + 1}`] = formatGravure((this.etat.noms[i] || '').trim()) || (this.t.sansGravure || '— sans gravure —');
      }
      // Espace entreprises : nom de l'entreprise et date souhaitée (facultatifs)
      this.querySelectorAll('[data-propriete]').forEach((champ) => {
        const v = (champ.value || '').trim();
        if (v) props[champ.dataset.propriete] = champ.type === 'date' ? new Date(`${v}T12:00:00`).toLocaleDateString('fr-CH') : v;
      });
      props._gravure = this.etat.mode;
      if (this.cfg.espace) props._espace = this.cfg.espace;
      props._bsp = Math.random().toString(36).slice(2, 10);
      return props;
    }

    // Noms de toutes les fourchettes du lot, dans l'ordre (mode « même texte » : répété).
    nomsDuLot() {
      const vide = this.t.sansGravure || '— sans gravure —';
      const q = this.etat.quantite;
      if (this.etat.mode === 'identique') return Array(q).fill(formatGravure(this.etat.identique.trim()));
      return Array.from({ length: q }, (_, i) => formatGravure((this.etat.noms[i] || '').trim()) || vide);
    }

    // Lignes de panier d'un lot à deux prix : prix normal jusqu'à la (des−1)e fourchette, prix réduit ensuite.
    lignesTranche(noms, identique, base) {
      const tr = this.cfg.tranche;
      const n1 = Math.min(noms.length, tr.des - 1);
      return [[this.cfg.varianteId, 0, n1], [tr.varianteId, n1, noms.length]].filter(([, a, b]) => b > a).map(([id, a, b]) => {
        const g = {};
        if (identique !== null) g[(this.t.cleIdentique || 'Gravure (identique ×{n})').replace('{n}', b - a)] = identique;
        else for (let i = a; i < b; i++) g[`Gravure ${i + 1}`] = noms[i];
        return { id, quantity: b - a, properties: { ...g, ...base } };
      });
    }

    async ajouter(confirmeVides) {
      const vides = this.nomsVides();
      const zoneVides = this.$('[data-vides]');
      if (vides.length && this.etat.mode === 'identique') { this.focusPremierVide(); return; }
      if (vides.length && !confirmeVides) {
        if (zoneVides) {
          const n = vides.length;
          const q = this.etat.quantite;
          const txt = zoneVides.querySelector('[data-vides-texte]');
          if (txt) txt.textContent = ((n > 1 ? this.t.videsPluriel : this.t.videsSingulier) || '{n} fourchette(s) sur {q} sans nom.').replace('{n}', n).replace('{q}', q);
          // « Retirer » n'a pas de sens si aucune fourchette n'a de nom
          const retirer = zoneVides.querySelector('[data-vides-retirer]');
          if (retirer) {
            retirer.hidden = n >= q;
            retirer.textContent = ((n > 1 ? this.t.retirerVidesPluriel : this.t.retirerVidesSingulier) || 'Retirer les {n} fourchettes sans nom').replace('{n}', n);
          }
          zoneVides.hidden = false;
          if (this.etat.saisie === 'champs') vides.forEach((i) => { const r = this.liste.querySelector(`[data-rangee="${i}"]`); r && r.classList.add('is-erreur'); });
          zoneVides.scrollIntoView({ block: 'nearest', behavior: reduitMouvement ? 'auto' : 'smooth' });
        }
        return;
      }
      if (zoneVides) zoneVides.hidden = true;

      // Pochette à votre logo : le fichier est demandé avant d'ajouter au panier
      const veutLogo = this.logoDemande();
      if (veutLogo && !this.logo && !this.etat.logoExistant && !this.logoParEmail) {
        this.alerteLogo(this.t.logoManquant);
        const zoneLogo = this.$('[data-logo]');
        zoneLogo && zoneLogo.scrollIntoView({ block: 'center', behavior: reduitMouvement ? 'auto' : 'smooth' });
        return;
      }

      const erreur = this.$('[data-erreur]');
      if (erreur) erreur.hidden = true;
      this.bouton.classList.add('is-chargement');
      this.bouton.setAttribute('aria-busy', 'true');

      const props = this.proprietes();
      // Nouvel essai après un envoi de logo réussi : on garde le même lot pour ne pas doubler la pochette.
      if (this._logoEnvoye) props._bsp = this._logoEnvoye;
      let items = [{ id: this.cfg.varianteId, quantity: this.etat.quantite, properties: props }];
      const aRetirer = [].concat(this.etat.modifier || []);
      if (this.cfg.tranche) {
        // Une seule commande groupée par produit : un nouvel ajout rejoint le lot déjà au panier,
        // pour que le prix réduit (dès la Ne fourchette) se compte sur l'ensemble des fourchettes.
        const vide = this.t.sansGravure || '— sans gravure —';
        let noms = this.nomsDuLot();
        let identique = this.etat.mode === 'identique' ? formatGravure(this.etat.identique.trim()) : null;
        const base = Object.fromEntries(Object.entries(props).filter(([k]) => !/^Gravure /.test(k)));
        if (!this.etat.modifier) {
          const panier = await this.lirePanier();
          const existante = panier && panier.items.find((it) => it.product_id === this.cfg.produitId && it.properties && it.properties._gravure);
          if (existante) {
            const lot = this.lignesDuLot(panier.items, existante);
            const avant = this.nomsDeLignes(lot);
            noms = avant.noms.map((n) => n || vide).concat(noms);
            identique = avant.identique !== null && avant.identique === identique ? identique : null;
            // Les champs remplis avant (établissement, date) restent si ce nouvel ajout ne les remplit pas.
            Object.entries(lot[0].properties).forEach(([k, v]) => { if (!/^Gravure /.test(k) && !k.startsWith('_') && !(k in base)) base[k] = v; });
            // Même identifiant de lot : les pochettes déjà au panier restent rattachées.
            if (lot[0].properties._bsp) { base._bsp = lot[0].properties._bsp; props._bsp = base._bsp; }
            lot.forEach((it) => aRetirer.push(it.key));
          }
        }
        base._gravure = identique !== null ? 'identique' : 'unique';
        items = this.lignesTranche(noms, identique, base);
      }
      // Chaque modèle de pochette sur sa ligne, rattaché au lot pour « Modifier » depuis le panier
      let ligneLogo = null;
      this.pochettesChoisies().forEach(({ p, n }) => {
        const proprietes = { _lot: props._bsp };
        if (p.propriete) proprietes[p.propriete.cle] = p.propriete.valeur;
        if (p.id === 'p3' && veutLogo) {
          // Un fichier ne passe pas en JSON : la pochette à votre logo part à part, en formulaire (multipart).
          if (this.logo) { ligneLogo = { id: p.varianteId, quantity: n, properties: proprietes }; return; }
          proprietes.Logo = this.etat.logoExistant || this.t.logoParEmail || 'À envoyer par e-mail';
        }
        items.push({ id: p.varianteId, quantity: n, properties: proprietes });
      });

      try {
        if (ligneLogo && !this._logoEnvoye) {
          const corps = new FormData();
          corps.append('id', ligneLogo.id);
          corps.append('quantity', ligneLogo.quantity);
          Object.keys(ligneLogo.properties).forEach((k) => corps.append(`properties[${k}]`, ligneLogo.properties[k]));
          corps.append('properties[Logo]', this.logo, this.logo.name);
          let ok = false;
          try {
            const rl = await fetch(`${window.BSP.routes.ajouter}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: corps });
            const dl = await rl.json().catch(() => ({}));
            ok = rl.ok && !dl.status;
          } catch (e) { ok = false; }
          if (!ok) {
            // Rien n'est encore au panier : on propose de commander et d'envoyer le logo par e-mail.
            this.alerteLogo(this.t.logoEchec);
            const secours = this.$('[data-logo-secours]');
            if (secours) secours.hidden = false;
            const zoneLogo = this.$('[data-logo]');
            zoneLogo && zoneLogo.scrollIntoView({ block: 'center', behavior: reduitMouvement ? 'auto' : 'smooth' });
            this.bouton.classList.remove('is-chargement');
            return;
          }
          this._logoEnvoye = props._bsp;
        }
        // Lignes remplacées : le lot modifié (et ses pochettes), ou le lot existant qu'on regroupe.
        if (aRetirer.length || this.etat.modifierPochettes.length) {
          const updates = {};
          aRetirer.forEach((k) => { updates[k] = 0; });
          this.etat.modifierPochettes.forEach((k) => { updates[k] = 0; });
          await fetch(`${window.BSP.routes.maj}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates }) });
        }
        const r = await fetch(`${window.BSP.routes.ajouter}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: 'bsp-panier-tiroir', sections_url: location.pathname }),
        });
        const data = await r.json();
        if (!r.ok || data.status) throw new Error(data.description || data.message || 'Erreur');
        // Espaces entreprises / revendeurs : l'espace, l'établissement et la date souhaitée aussi au niveau
        // de la commande (« Détails supplémentaires » dans l'admin, e-mail de confirmation, facture).
        if (this.cfg.espace) {
          const attributes = { Espace: this.cfg.espace === 'revendeurs' ? 'Restaurants & revendeurs' : 'Entreprises' };
          const source = (items[0] && items[0].properties) || props;
          Object.keys(source).filter((k) => !k.startsWith('_') && !/^Gravure /.test(k)).forEach((k) => { attributes[k] = source[k]; });
          await fetch(`${window.BSP.routes.maj}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes }) }).catch(() => {});
        }
        this.oublier();
        this.etat.modifier = null;
        this.etat.modifierPochettes = [];
        this.etat.logoExistant = null;
        this._logoEnvoye = null;
        this.logoParEmail = false;
        if (window.BSP.majPanier) window.BSP.majPanier(data.sections, { ouvrir: true });
        else location.href = window.BSP.routes.panier;
        this.bouton.classList.remove('is-chargement');
        this.bouton.classList.add('is-ajoute');
        const etiquette = this.$('[data-libelle-bouton]');
        const ancien = etiquette && etiquette.textContent;
        if (etiquette) etiquette.textContent = this.t.ajoute || 'Ajouté au panier';
        setTimeout(() => { this.bouton.classList.remove('is-ajoute'); if (etiquette) etiquette.textContent = this.t.ajouter || ancien; }, 2400);
        const bandeau = this.$('[data-bandeau-modif]');
        if (bandeau) bandeau.hidden = true;
        if (new URLSearchParams(location.search).has('modifier')) history.replaceState(null, '', location.pathname);
        this.lirePanier();
      } catch (e) {
        this.bouton.classList.remove('is-chargement');
        if (erreur) { erreur.textContent = e.message || this.t.erreur; erreur.hidden = false; }
      } finally {
        this.bouton.removeAttribute('aria-busy');
      }
    }
  }

  /* ─── Cascade : des fourchettes gravées l'une après l'autre ──────── */
  class BSPCascade extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-texte]')).map((el) => ({ el, fk: new BSPFourchette(el.querySelector('.bsp-fk')), texte: el.dataset.texte }));
      const toutGraver = (animer) => this.items.forEach((it, i) => setTimeout(() => it.fk.graver(it.texte, { animer }), animer ? i * 420 : 0));
      if (reduitMouvement || !('IntersectionObserver' in window) || document.body.classList.contains('shopify-design-mode')) { toutGraver(false); return; }
      this._io = new IntersectionObserver(([e]) => {
        if (!e.isIntersecting) return;
        this._io.disconnect();
        setTimeout(() => toutGraver(true), 350);
      }, { threshold: 0.35 });
      this._io.observe(this);
    }
    disconnectedCallback() { this._io && this._io.disconnect(); }
  }

  if (!customElements.get('bsp-cascade')) customElements.define('bsp-cascade', BSPCascade);
  if (!customElements.get('bsp-demo-gravure')) customElements.define('bsp-demo-gravure', BSPDemoGravure);
  if (!customElements.get('bsp-configurateur')) customElements.define('bsp-configurateur', BSPConfigurateur);

  // Aperçus statiques (panier, sections) : ajuster la taille du texte gravé.
  const initStatiques = (racine) => racine.querySelectorAll('.bsp-fk[data-bsp-fk-statique]').forEach((el) => { if (!el._bsp) el._bsp = new BSPFourchette(el); });
  initStatiques(document);
  document.addEventListener('bsp:panier:rendu', (e) => initStatiques(e.detail && e.detail.racine ? e.detail.racine : document));
  document.addEventListener('shopify:section:load', (e) => initStatiques(e.target));

  window.BSPGravure = { BSPFourchette, nettoyer, formatPrix, formatGravure, decoderTextes, initStatiques };
})();
